import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Lock, ChevronDown, Plus, Trash2 } from 'lucide-react';
import { PlatformIcon } from '../common/PlatformIcon';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { useChannelConnect } from '../reach/ChannelPicker';
import {
  GET_SOCIAL_PROFILES,
  UPDATE_SOCIAL_PROFILE,
  GET_AUTOMATION_RULES,
  CREATE_AUTOMATION_RULE,
  UPDATE_AUTOMATION_RULE,
  CREATE_POSTING_CONTENT_AGE,
  UPDATE_POSTING_CONTENT_AGE,
  DELETE_POSTING_CONTENT_AGE,
  CREATE_POSTING_SCHEDULE_PREFERENCE,
  UPDATE_POSTING_SCHEDULE_PREFERENCE,
  DELETE_POSTING_SCHEDULE_PREFERENCE,
  CREATE_POSTING_TIME_WINDOW,
  UPDATE_POSTING_TIME_WINDOW,
  DELETE_POSTING_TIME_WINDOW,
} from '../../graphql';
import {
  SocialProfile,
  AutomationRule,
  PostingContentAge,
  PostingSchedulePreference,
  PostingTimeWindow,
  Platform,
} from '../../types';
import { GetSocialProfilesResponse, GetAutomationRulesResponse } from '../../types/graphql';
import { useAccount } from '../../context/AccountContext';
import { Loader } from '../common/PageLoader';

interface NewAutomationRule {
  name: string;
  type: string;
  priority: number;
}

interface ChildRecordOperations {
  contentAge: {
    added: Partial<PostingContentAge> | null;
    updated: Partial<PostingContentAge> | null;
    deleted: PostingContentAge | null;
  };
  schedulePreferences: {
    added: Array<PostingSchedulePreference & { tempId?: string }>;
    updated: Array<PostingSchedulePreference & { tempId?: string }>;
    deleted: Array<PostingSchedulePreference & { tempId?: string }>;
  };
  timeWindows: {
    added: Array<PostingTimeWindow & { tempId?: string }>;
    updated: Array<PostingTimeWindow & { tempId?: string }>;
    deleted: Array<PostingTimeWindow & { tempId?: string }>;
  };
}

interface EditingRule
  extends Omit<
    AutomationRule,
    'postingContentAge' | 'postingSchedulePreferences' | 'postingTimeWindows'
  > {
  postingContentAge: Array<PostingContentAge & { tempId?: string }> | undefined;
  postingSchedulePreferences?: Array<PostingSchedulePreference & { tempId?: string }>;
  postingTimeWindows?: Array<PostingTimeWindow & { tempId?: string }>;
}

export function SocialSettings() {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedProfile, setSelectedProfile] = useState<SocialProfile | null>(null);
  const [automationRulesDialog, setAutomationRulesDialog] = useState<boolean>(false);
  const [newRuleDialog, setNewRuleDialog] = useState<boolean>(false);
  const [editRuleDialog, setEditRuleDialog] = useState<boolean>(false);
  const [editingRule, setEditingRule] = useState<EditingRule | null>(null);
  const [childRecordOperations, setChildRecordOperations] = useState<ChildRecordOperations>({
    contentAge: { added: null, updated: null, deleted: null },
    schedulePreferences: { added: [], updated: [], deleted: [] },
    timeWindows: { added: [], updated: [], deleted: [] },
  });
  const [modifiedTimeWindows, setModifiedTimeWindows] = useState<Set<string>>(new Set());
  const [modifiedSchedulePreferences, setModifiedSchedulePreferences] = useState<Set<string>>(
    new Set()
  );

  const [newAutomationRule, setNewAutomationRule] = useState<NewAutomationRule>({
    name: '',
    type: '',
    priority: 0,
  });

  const { accountId, isLoading: accountsLoading, refetchAccounts } = useAccount();

  const {
    data: socialProfilesData,
    loading,
    refetch,
  } = useQuery<GetSocialProfilesResponse>(GET_SOCIAL_PROFILES, {
    variables: { accountId },
    skip: !accountId,
  });

  const {
    data: automationRulesData,
    loading: automationRulesLoading,
    refetch: refetchRules,
  } = useQuery<GetAutomationRulesResponse>(GET_AUTOMATION_RULES, {
    variables: { accountId },
    skip: !accountId || !selectedProfile,
  });

  // The sign-in popup comes back with the channels it can post to; the
  // person picks (several) or the one is connected at once. Rows exist only
  // then, so there is nothing to create before the popup.
  const { start: startChannelSignIn, picker: channelPicker } = useChannelConnect({
    accountId,
    onConnected: () => {
      refetch();
      refetchAccounts();
    },
  });

  const [updateSocialProfile] = useMutation(UPDATE_SOCIAL_PROFILE, {
    onCompleted: () => {
      refetch();
    },
    onError: (error) => {
      console.error('Error updating social profile:', error);
    },
  });

  const [createAutomationRule] = useMutation(CREATE_AUTOMATION_RULE, {
    onCompleted: () => {
      setNewRuleDialog(false);
      setNewAutomationRule({
        name: '',
        type: '',
        priority: 0,
      });
      refetchRules();
    },
    onError: (error) => {
      console.error('Error creating automation rule:', error);
    },
  });

  const [updateAutomationRule] = useMutation(UPDATE_AUTOMATION_RULE, {
    onCompleted: () => {
      handleCloseEditDialog();
      refetchRules();
    },
    onError: (error) => {
      console.error('Error updating automation rule:', error);
    },
  });

  const [createContentAge] = useMutation(CREATE_POSTING_CONTENT_AGE, {
    onCompleted: () => refetchRules(),
  });
  const [updateContentAge] = useMutation(UPDATE_POSTING_CONTENT_AGE, {
    onCompleted: () => refetchRules(),
  });
  const [deleteContentAge] = useMutation(DELETE_POSTING_CONTENT_AGE, {
    onCompleted: () => refetchRules(),
  });

  const [createSchedulePreference] = useMutation(CREATE_POSTING_SCHEDULE_PREFERENCE);
  const [updateSchedulePreference] = useMutation(UPDATE_POSTING_SCHEDULE_PREFERENCE);
  const [deleteSchedulePreference] = useMutation(DELETE_POSTING_SCHEDULE_PREFERENCE);

  const [createTimeWindow] = useMutation(CREATE_POSTING_TIME_WINDOW);
  const [updateTimeWindow] = useMutation(UPDATE_POSTING_TIME_WINDOW);
  const [deleteTimeWindow] = useMutation(DELETE_POSTING_TIME_WINDOW);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handlePlatformSelect = async (platform: string) => {
    if (!accountId) {
      return;
    }
    handleCloseDialog();
    await startChannelSignIn(platform);
  };

  /*const handleShowAutomationRules = (profile: SocialProfile) => {
    setSelectedProfile(profile);
    setAutomationRulesDialog(true);
    refetchRules();
  };*/

  const handleCloseAutomationRules = () => {
    setSelectedProfile(null);
    setAutomationRulesDialog(false);
    setNewAutomationRule({
      name: '',
      type: '',
      priority: 0,
    });
  };

  const handleEditRule = (rule: AutomationRule) => {
    const activeContentAge =
      rule.postingContentAge?.find((ca) => ca.isActive) || rule.postingContentAge?.[0];

    setEditingRule({
      ...rule,
      postingContentAge: activeContentAge ? [activeContentAge] : undefined,
      postingSchedulePreferences: rule.postingSchedulePreferences || [],
      postingTimeWindows: rule.postingTimeWindows || [],
    });
    setChildRecordOperations({
      contentAge: { added: null, updated: null, deleted: null },
      schedulePreferences: { added: [], updated: [], deleted: [] },
      timeWindows: { added: [], updated: [], deleted: [] },
    });
    setModifiedTimeWindows(new Set());
    setModifiedSchedulePreferences(new Set());
    setEditRuleDialog(true);
  };

  const handleCloseEditDialog = () => {
    setEditRuleDialog(false);
    setEditingRule(null);
  };

  const handleSaveEdit = async () => {
    if (!editingRule || !editingRule.name || !editingRule.type) {
      return;
    }

    try {
      await updateAutomationRule({
        variables: {
          id: editingRule.id,
          input: {
            name: editingRule.name,
            priority: editingRule.priority,
            type: editingRule.type,
          },
        },
      });

      if (childRecordOperations.contentAge.added) {
        await createContentAge({
          variables: {
            input: {
              postingRuleId: editingRule.id,
              ...childRecordOperations.contentAge.added,
            },
          },
        });
      } else if (
        childRecordOperations.contentAge.updated &&
        editingRule.postingContentAge?.[0]?.id
      ) {
        await updateContentAge({
          variables: {
            id: editingRule.postingContentAge[0].id,
            input: childRecordOperations.contentAge.updated,
          },
        });
      } else if (
        childRecordOperations.contentAge.deleted &&
        editingRule.postingContentAge?.[0]?.id
      ) {
        await deleteContentAge({
          variables: { id: editingRule.postingContentAge[0].id },
        });
      }

      for (const prefId of modifiedSchedulePreferences) {
        const currentPref = editingRule.postingSchedulePreferences?.find(
          (p) => p.tempId === prefId || p.id === prefId
        );

        if (currentPref) {
          if (currentPref.tempId) {
            const { tempId, __typename, id, postingRuleId, ...prefData } =
              currentPref as PostingSchedulePreference & { tempId?: string; __typename?: string };
            await createSchedulePreference({
              variables: {
                input: {
                  postingRuleId: editingRule.id,
                  ...prefData,
                },
              },
            });
          } else if (currentPref.id) {
            const { tempId, __typename, id, postingRuleId, ...prefData } =
              currentPref as PostingSchedulePreference & { tempId?: string; __typename?: string };
            await updateSchedulePreference({
              variables: {
                id: currentPref.id,
                input: prefData,
              },
            });
          }
        }
      }

      for (const pref of childRecordOperations.schedulePreferences.deleted) {
        if (pref.id) {
          await deleteSchedulePreference({
            variables: { id: pref.id },
          });
        }
      }

      for (const windowId of modifiedTimeWindows) {
        const currentWindow = editingRule.postingTimeWindows?.find(
          (w) => w.tempId === windowId || w.id === windowId
        );

        if (currentWindow) {
          if (currentWindow.tempId) {
            const { tempId, postingRuleId, ...windowData } = currentWindow;
            await createTimeWindow({
              variables: {
                input: {
                  postingRuleId: editingRule.id,
                  ...windowData,
                },
              },
            });
          } else if (currentWindow.id) {
            const { tempId, __typename, id, postingRuleId, ...windowData } =
              currentWindow as PostingTimeWindow & { tempId?: string; __typename?: string };
            await updateTimeWindow({
              variables: {
                id: currentWindow.id,
                input: windowData,
              },
            });
          }
        }
      }

      for (const window of childRecordOperations.timeWindows.deleted) {
        if (window.id) {
          await deleteTimeWindow({
            variables: { id: window.id },
          });
        }
      }

      await refetchRules();
    } catch (error) {
      console.error('Error updating automation rule:', error);
    }
  };

  const handleCreateAutomationRule = async () => {
    if (!newAutomationRule.name || !newAutomationRule.type || !selectedProfile || !accountId) {
      return;
    }

    try {
      await createAutomationRule({
        variables: {
          input: {
            accountId,
            socialProfileId: selectedProfile.id,
            ...newAutomationRule,
          },
        },
      });
    } catch (error) {
      console.error('Error creating automation rule:', error);
    }
  };

  const trackContentAgeChange = (
    operation: 'added' | 'updated' | 'deleted',
    data: Partial<PostingContentAge> | PostingContentAge
  ) => {
    setChildRecordOperations((prev) => ({
      ...prev,
      contentAge: { ...prev.contentAge, [operation]: data },
    }));
  };

  const trackSchedulePreferenceChange = (
    operation: 'added' | 'updated' | 'deleted',
    data: PostingSchedulePreference & { tempId?: string }
  ) => {
    setChildRecordOperations((prev) => ({
      ...prev,
      schedulePreferences: {
        ...prev.schedulePreferences,
        [operation]:
          operation === 'deleted'
            ? [...(prev.schedulePreferences[operation] || []), data]
            : operation === 'added'
              ? [...(prev.schedulePreferences[operation] || []), data]
              : (prev.schedulePreferences[operation] || []).map((item) =>
                  (item.id && item.id === data.id) || (item.tempId && item.tempId === data.tempId)
                    ? { ...item, ...data }
                    : item
                ),
      },
    }));
  };

  const trackTimeWindowChange = (
    operation: 'added' | 'updated' | 'deleted',
    data: PostingTimeWindow & { tempId?: string }
  ) => {
    if (operation === 'added') {
      setChildRecordOperations((prev) => ({
        ...prev,
        timeWindows: {
          ...prev.timeWindows,
          added: [...(prev.timeWindows.added || []), data],
        },
      }));
      setModifiedTimeWindows((prev) => new Set([...prev, data.tempId || data.id]));
    } else if (operation === 'updated') {
      setModifiedTimeWindows((prev) => new Set([...prev, data.tempId || data.id]));
    } else if (operation === 'deleted') {
      setChildRecordOperations((prev) => ({
        ...prev,
        timeWindows: {
          ...prev.timeWindows,
          deleted: [...(prev.timeWindows.deleted || []), data],
        },
      }));
    }
  };

  if (accountsLoading || loading)
    return <Loader label="Loading social accounts" className="text-cmp-ink" />;
  if (!accountId) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="mb-1">Connected Accounts</h2>
          <p className="text-muted-foreground">
            No accounts found. Please create an account first.
          </p>
        </div>
      </div>
    );
  }

  const getPlatformColor = (platform: string | undefined): string => {
    switch (platform?.toLowerCase()) {
      case 'facebook':
        return '#1877F2';
      case 'instagram':
        return '#E4405F';
      case 'x':
        return '#000000';
      case 'threads':
        return '#000000';
      case 'youtube':
        return '#FF0000';
      case 'tiktok':
        return '#000000';
      case 'snapchat':
        return '#FFFC00';
      case 'reddit':
        return '#FF4500';
      case 'bluesky':
        return '#0560FF';
      case 'whatsapp':
        return '#25D366';
      case 'linkedin':
        return '#0A66C2';
      default:
        return '#f2efe0';
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 mb-6">
        <h2 className="mb-1">Connected Accounts</h2>
        <p className="text-muted-foreground">
          Manage your connected social media profiles and integrations.
        </p>
      </div>

      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex justify-between items-center mb-6 flex-shrink-0">
          <h3 className="text-lg font-semibold text-cmp-ink">Social Accounts</h3>
          <Button onClick={handleOpenDialog} className="gap-2">
            <Plus className="w-4 h-4" />
            New Social Account
          </Button>
        </div>

        <div className="flex-1 min-h-0 overflow-auto pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {socialProfilesData?.socialProfiles.map((profile) => (
              <div
                key={profile.id}
                className="p-4 flex flex-col items-center gap-2 relative bg-core-dark rounded shadow-lg hover:-translate-y-1 transition-transform duration-200"
              >
                {!profile.isActive && (
                  <div className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-error" />
                )}
                <div
                  className="w-12 h-12 flex items-center justify-center"
                  style={{ color: getPlatformColor(profile.platform) }}
                >
                  <PlatformIcon platform={profile.platform as Platform} size={48} />
                </div>
                <p className="mt-2 font-medium text-center text-cmp-ink capitalize">
                  {profile.profileName}
                </p>
                <p className="text-xs text-cmp-ink-2 text-center opacity-80">
                  Connected {new Date(profile.createdAt || '').toLocaleDateString()}
                </p>
                <div className="mt-auto pt-2 border-t border-cmp-line-2 w-full flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-cmp-ink">Active</span>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-medium ${
                          profile.isActive ? 'text-success' : 'text-cmp-ink-3'
                        }`}
                      >
                        {profile.isActive ? 'ON' : 'OFF'}
                      </span>
                      <Switch
                        checked={profile.isActive}
                        onCheckedChange={(checked) => {
                          updateSocialProfile({
                            variables: {
                              id: profile.id,
                              input: {
                                profileName: profile.profileName,
                                profileUsername: profile.profileUsername || '',
                                profileLink: profile.profileLink || '',
                                isActive: checked,
                                isPublishing: profile.isPublishing,
                              },
                            },
                          });
                        }}
                        className={!profile.isActive ? 'border border-cmp-line-2' : ''}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-cmp-ink">Publishing</span>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-medium ${
                          profile.isPublishing ? 'text-success' : 'text-cmp-ink-3'
                        }`}
                      >
                        {profile.isPublishing ? 'ON' : 'OFF'}
                      </span>
                      <Switch
                        checked={profile.isPublishing}
                        onCheckedChange={(checked) => {
                          updateSocialProfile({
                            variables: {
                              id: profile.id,
                              input: {
                                profileName: profile.profileName,
                                profileUsername: profile.profileUsername || '',
                                profileLink: profile.profileLink || '',
                                isActive: profile.isActive,
                                isPublishing: checked,
                              },
                            },
                          });
                        }}
                        className={!profile.isPublishing ? 'border border-cmp-line-2' : ''}
                      />
                    </div>
                  </div>
                </div>
                {[
                  'x',
                  'facebook',
                  'threads',
                  'instagram',
                  'youtube',
                  'tiktok',
                  'snapchat',
                ].includes(profile.platform?.toLowerCase() || '') && (
                  <button
                    onClick={() => startChannelSignIn(profile.platform)}
                    className="mt-2 w-full px-3 py-1.5 border border-cmp-line-2 text-cmp-ink rounded text-xs hover:bg-form-dark flex items-center justify-center gap-1 transition-colors"
                  >
                    <Lock className="w-9" />
                    Reauthorize
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {openDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-cmp-raise rounded-cmp3 shadow-cmp-e3 border border-cmp-line max-w-2xl w-full m-4">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-cmp-ink mb-4">Add Social Account</h3>
              <p className="text-sm text-cmp-ink-2 mb-6">
                Select a platform to connect. Clicking an icon opens its sign-in; you then pick
                which accounts or pages to connect.
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                {[
                  { platform: 'facebook', label: 'Facebook' },
                  { platform: 'x', label: 'X' },
                  { platform: 'threads', label: 'Threads' },
                  { platform: 'instagram', label: 'Instagram' },
                  { platform: 'youtube', label: 'YouTube' },
                  { platform: 'tiktok', label: 'TikTok' },
                  { platform: 'snapchat', label: 'Snapchat' },
                  { platform: 'reddit', label: 'Reddit' },
                  { platform: 'bluesky', label: 'BlueSky' },
                  { platform: 'whatsapp', label: 'WhatsApp' },
                  { platform: 'linkedin', label: 'LinkedIn' },
                ].map(({ platform, label }) => (
                  <button
                    key={platform}
                    onClick={() => handlePlatformSelect(platform)}
                    className="flex flex-col items-center gap-2 p-4 border border-cmp-line-2 rounded-lg hover:border-primary hover:bg-form-dark transition-all duration-200 group"
                  >
                    <div
                      className="w-12 h-12 flex items-center justify-center"
                      style={{ color: getPlatformColor(platform) }}
                    >
                      <PlatformIcon platform={platform as Platform} size={48} />
                    </div>
                    <span className="text-xs text-cmp-ink text-center group-hover:text-primary transition-colors">
                      {label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 p-6 border-t border-cmp-line-2">
              <button
                onClick={handleCloseDialog}
                className="px-4 py-2 text-cmp-ink border border-cmp-line-2 rounded hover:bg-form-dark transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {automationRulesDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-cmp-raise rounded-cmp3 shadow-cmp-e3 border border-cmp-line max-w-2xl w-full m-4 max-h-[90vh] overflow-auto">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-cmp-ink mb-4">
                Automations for {selectedProfile?.profileName}
              </h3>
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-semibold text-cmp-ink">Rules</h4>
                <button
                  onClick={() => setNewRuleDialog(true)}
                  className="px-3 py-1.5 bg-button-primary text-button-primary rounded text-sm font-medium hover:bg-primary-medium"
                >
                  New Rule
                </button>
              </div>

              {automationRulesLoading ? (
                <Loader label="Loading automations" className="text-cmp-ink" />
              ) : (
                <div>
                  {automationRulesData?.automationRules
                    .filter((rule) => rule.socialProfileId === selectedProfile?.id)
                    .sort((a, b) => a.priority - b.priority)
                    .map((rule) => (
                      <div
                        key={rule.id}
                        className="p-4 mb-4 border border-cmp-line-2 rounded bg-form-dark"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-cmp-ink font-medium">{rule.name}</p>
                            <p className="text-sm text-cmp-ink-2">Type: {rule.type}</p>
                            <p className="text-xs text-cmp-ink-2">Priority: {rule.priority}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditRule(rule)}
                              className="px-3 py-1 text-xs border border-cmp-line-2 text-cmp-ink rounded hover:bg-cmp-bronze-soft"
                            >
                              Edit
                            </button>
                            <span
                              className={`text-xs font-bold uppercase ${
                                rule.isActive ? 'text-success' : 'text-error'
                              }`}
                            >
                              {rule.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  {(!automationRulesData?.automationRules ||
                    automationRulesData.automationRules.filter(
                      (rule) => rule.socialProfileId === selectedProfile?.id
                    ).length === 0) && (
                    <p className="p-4 text-center text-cmp-ink-2">
                      No automations found for this profile
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end p-6 border-t border-cmp-line-2">
              <button
                onClick={handleCloseAutomationRules}
                className="px-4 py-2 bg-button-secondary text-button-secondary rounded font-medium hover:bg-primary-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {newRuleDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-cmp-raise rounded-cmp3 shadow-cmp-e3 border border-cmp-line max-w-2xl w-full m-4 max-h-[90vh] overflow-auto">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-cmp-ink mb-4">
                Create New Automation Rule
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-cmp-ink mb-1">Rule Name</label>
                  <input
                    type="text"
                    value={newAutomationRule.name}
                    onChange={(e) =>
                      setNewAutomationRule((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-cmp-ink mb-1">Rule Type</label>
                  <select
                    value={newAutomationRule.type}
                    onChange={(e) =>
                      setNewAutomationRule((prev) => ({ ...prev, type: e.target.value }))
                    }
                    className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="" className="bg-form-dark">
                      Select type
                    </option>
                    <option value="auto_post" className="bg-form-dark">
                      Auto Post
                    </option>
                    <option value="auto_reply" className="bg-form-dark">
                      Auto Reply
                    </option>
                    <option value="auto_moderate" className="bg-form-dark">
                      Auto Moderate
                    </option>
                    <option value="auto_schedule" className="bg-form-dark">
                      Auto Schedule
                    </option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-cmp-ink mb-1">Priority</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={newAutomationRule.priority}
                    onChange={(e) =>
                      setNewAutomationRule((prev) => ({
                        ...prev,
                        priority: parseInt(e.target.value) || 0,
                      }))
                    }
                    className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-6 border-t border-cmp-line-2">
              <button
                onClick={() => setNewRuleDialog(false)}
                className="px-4 py-2 text-cmp-ink border border-cmp-line-2 rounded hover:bg-form-dark transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAutomationRule}
                disabled={!newAutomationRule.name || !newAutomationRule.type}
                className="px-4 py-2 bg-button-primary text-button-primary rounded font-medium hover:bg-primary-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Create Automation
              </button>
            </div>
          </div>
        </div>
      )}

      {editRuleDialog && editingRule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-cmp-raise rounded-cmp3 shadow-cmp-e3 border border-cmp-line max-w-4xl w-full m-4 max-h-[90vh] overflow-auto">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-cmp-ink mb-4">
                Edit Automation Rule: {editingRule.name}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-cmp-ink mb-1">Rule Name</label>
                  <input
                    type="text"
                    value={editingRule.name || ''}
                    onChange={(e) =>
                      setEditingRule((prev) => (prev ? { ...prev, name: e.target.value } : null))
                    }
                    className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-cmp-ink mb-1">Rule Type</label>
                  <select
                    value={editingRule.type || ''}
                    onChange={(e) =>
                      setEditingRule((prev) => (prev ? { ...prev, type: e.target.value } : null))
                    }
                    className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="auto_post" className="bg-form-dark">
                      Auto Post
                    </option>
                    <option value="auto_reply" className="bg-form-dark">
                      Auto Reply
                    </option>
                    <option value="auto_moderate" className="bg-form-dark">
                      Auto Moderate
                    </option>
                    <option value="auto_schedule" className="bg-form-dark">
                      Auto Schedule
                    </option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-cmp-ink mb-1">Priority</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={editingRule.priority || 0}
                    onChange={(e) =>
                      setEditingRule((prev) =>
                        prev ? { ...prev, priority: parseInt(e.target.value) || 0 } : null
                      )
                    }
                    className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-cmp-ink mb-1">
                    Database Filter Rule (SQL Syntax)
                  </label>
                  <textarea
                    rows={4}
                    value={editingRule.dbFilterRule || ''}
                    disabled
                    className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary opacity-50 font-mono text-sm"
                  />
                </div>

                <details className="border border-cmp-line-2 rounded">
                  <summary className="cursor-pointer p-3 text-cmp-ink font-semibold flex items-center gap-2 list-none">
                    <ChevronDown className="w-9" />
                    <span>Posting Content Age</span>
                  </summary>
                  <div className="p-4 flex flex-col gap-4">
                    {(!editingRule.postingContentAge ||
                      editingRule.postingContentAge.length === 0) && (
                      <button
                        onClick={() => {
                          const newContentAge = {
                            minAgeHours: 0,
                            maxAgeHours: 24,
                            isImmediate: false,
                            delayOffsetHours: 0,
                          };
                          setEditingRule((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  postingContentAge: [
                                    newContentAge as PostingContentAge & {
                                      tempId?: string;
                                    },
                                  ],
                                }
                              : null
                          );
                          trackContentAgeChange('added', newContentAge);
                        }}
                        className="px-3 py-1.5 border border-cmp-line-2 text-cmp-ink rounded text-sm hover:bg-form-dark flex items-center gap-2"
                      >
                        <Plus className="w-9" />
                        Add Content Age Settings
                      </button>
                    )}
                    {editingRule.postingContentAge && editingRule.postingContentAge.length > 0 && (
                      <>
                        <button
                          onClick={() => {
                            const contentAge = editingRule.postingContentAge?.[0];
                            if (contentAge?.id) {
                              trackContentAgeChange('deleted', contentAge);
                            }
                            setEditingRule((prev) =>
                              prev ? { ...prev, postingContentAge: undefined } : null
                            );
                          }}
                          className="px-3 py-1.5 border border-error text-error rounded text-sm hover:bg-form-dark flex items-center gap-2"
                        >
                          <Trash2 className="w-9" />
                          Remove Content Age Settings
                        </button>
                        <div>
                          <label className="block text-sm font-medium text-cmp-ink mb-1">
                            Min Age (hours)
                          </label>
                          <input
                            type="number"
                            value={editingRule.postingContentAge[0]?.minAgeHours || ''}
                            onChange={(e) => {
                              const newValue = parseInt(e.target.value) || 0;
                              const updatedContentAge = {
                                ...editingRule.postingContentAge![0],
                                minAgeHours: newValue,
                              };
                              setEditingRule((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      postingContentAge: [
                                        updatedContentAge as PostingContentAge & {
                                          tempId?: string;
                                        },
                                      ],
                                    }
                                  : null
                              );
                              if (editingRule.postingContentAge?.[0]?.id) {
                                trackContentAgeChange('updated', updatedContentAge);
                              } else {
                                trackContentAgeChange('added', updatedContentAge);
                              }
                            }}
                            className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-cmp-ink mb-1">
                            Max Age (hours)
                          </label>
                          <input
                            type="number"
                            value={editingRule.postingContentAge[0]?.maxAgeHours || ''}
                            onChange={(e) => {
                              const newValue = parseInt(e.target.value) || 0;
                              const updatedContentAge = {
                                ...editingRule.postingContentAge![0],
                                maxAgeHours: newValue,
                              };
                              setEditingRule((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      postingContentAge: [
                                        updatedContentAge as PostingContentAge & {
                                          tempId?: string;
                                        },
                                      ],
                                    }
                                  : null
                              );
                              if (editingRule.postingContentAge?.[0]?.id) {
                                trackContentAgeChange('updated', updatedContentAge);
                              } else {
                                trackContentAgeChange('added', updatedContentAge);
                              }
                            }}
                            className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-cmp-ink mb-1">
                            Delay Offset (hours)
                          </label>
                          <input
                            type="number"
                            value={editingRule.postingContentAge[0]?.delayOffsetHours || ''}
                            onChange={(e) => {
                              const newValue = parseInt(e.target.value) || 0;
                              const updatedContentAge = {
                                ...editingRule.postingContentAge![0],
                                delayOffsetHours: newValue,
                              };
                              setEditingRule((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      postingContentAge: [
                                        updatedContentAge as PostingContentAge & {
                                          tempId?: string;
                                        },
                                      ],
                                    }
                                  : null
                              );
                              if (editingRule.postingContentAge?.[0]?.id) {
                                trackContentAgeChange('updated', updatedContentAge);
                              } else {
                                trackContentAgeChange('added', updatedContentAge);
                              }
                            }}
                            className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </details>

                <details className="border border-cmp-line-2 rounded">
                  <summary className="cursor-pointer p-3 text-cmp-ink font-semibold flex items-center gap-2 list-none">
                    <ChevronDown className="w-9" />
                    <span>
                      Posting Schedule Preferences (
                      {editingRule.postingSchedulePreferences?.length || 0})
                    </span>
                  </summary>
                  <div className="p-4 flex flex-col gap-4">
                    {editingRule.postingSchedulePreferences?.map((pref, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-cmp-ink mb-1">
                            Minute Mark
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="59"
                            value={pref.minuteMark}
                            onChange={(e) => {
                              const updatedPrefs = [
                                ...(editingRule.postingSchedulePreferences || []),
                              ];
                              updatedPrefs[index] = {
                                ...pref,
                                minuteMark: parseInt(e.target.value) || 0,
                              };
                              setEditingRule((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      postingSchedulePreferences: updatedPrefs,
                                    }
                                  : null
                              );
                              setModifiedSchedulePreferences(
                                (prev) => new Set([...prev, pref.tempId || pref.id])
                              );
                            }}
                            className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-cmp-ink mb-1">
                            Interval (minutes)
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={pref.intervalMinutes}
                            onChange={(e) => {
                              const updatedPrefs = [
                                ...(editingRule.postingSchedulePreferences || []),
                              ];
                              updatedPrefs[index] = {
                                ...pref,
                                intervalMinutes: parseInt(e.target.value) || 60,
                              };
                              setEditingRule((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      postingSchedulePreferences: updatedPrefs,
                                    }
                                  : null
                              );
                              if (pref.id) {
                                trackSchedulePreferenceChange('updated', updatedPrefs[index]);
                              } else {
                                trackSchedulePreferenceChange('added', updatedPrefs[index]);
                              }
                            }}
                            className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <button
                          onClick={() => {
                            if (pref.id) {
                              trackSchedulePreferenceChange('deleted', pref);
                            }
                            const updatedPrefs = (
                              editingRule.postingSchedulePreferences || []
                            ).filter((_, i) => i !== index);
                            setEditingRule((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    postingSchedulePreferences: updatedPrefs,
                                  }
                                : null
                            );
                          }}
                          className="p-2 rounded hover:bg-cmp-bronze-soft text-error mt-6"
                        >
                          <Trash2 className="w-9" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const newPref: PostingSchedulePreference & { tempId: string } = {
                          id: '',
                          postingRuleId: editingRule.id,
                          minuteMark: 0,
                          intervalMinutes: 60,
                          isActive: true,
                          tempId: `temp_${Date.now()}_${Math.random()}`,
                        };
                        const updatedPrefs = [
                          ...(editingRule.postingSchedulePreferences || []),
                          newPref,
                        ];
                        setEditingRule((prev) =>
                          prev
                            ? {
                                ...prev,
                                postingSchedulePreferences: updatedPrefs,
                              }
                            : null
                        );
                        setModifiedSchedulePreferences(
                          (prev) => new Set([...prev, newPref.tempId])
                        );
                      }}
                      className="px-3 py-1.5 border border-cmp-line-2 text-cmp-ink rounded text-sm hover:bg-form-dark flex items-center gap-2"
                    >
                      <Plus className="w-9" />
                      Add Schedule Preference
                    </button>
                  </div>
                </details>

                <details className="border border-cmp-line-2 rounded">
                  <summary className="cursor-pointer p-3 text-cmp-ink font-semibold flex items-center gap-2 list-none">
                    <ChevronDown className="w-9" />
                    <span>
                      Posting Time Windows ({editingRule.postingTimeWindows?.length || 0})
                    </span>
                  </summary>
                  <div className="p-4 flex flex-col gap-4">
                    {editingRule.postingTimeWindows?.map((window, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-cmp-ink mb-1">
                            Day of Week
                          </label>
                          <select
                            value={window.dayOfWeek}
                            onChange={(e) => {
                              const updatedWindows = [...(editingRule.postingTimeWindows || [])];
                              updatedWindows[index] = {
                                ...window,
                                dayOfWeek: parseInt(e.target.value),
                              };
                              setEditingRule((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      postingTimeWindows: updatedWindows,
                                    }
                                  : null
                              );
                              setModifiedTimeWindows(
                                (prev) => new Set([...prev, window.tempId || window.id])
                              );
                            }}
                            className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                          >
                            <option value={1} className="bg-form-dark">
                              Monday
                            </option>
                            <option value={2} className="bg-form-dark">
                              Tuesday
                            </option>
                            <option value={3} className="bg-form-dark">
                              Wednesday
                            </option>
                            <option value={4} className="bg-form-dark">
                              Thursday
                            </option>
                            <option value={5} className="bg-form-dark">
                              Friday
                            </option>
                            <option value={6} className="bg-form-dark">
                              Saturday
                            </option>
                            <option value={0} className="bg-form-dark">
                              Sunday
                            </option>
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-cmp-ink mb-1">
                            Start Time
                          </label>
                          <input
                            type="time"
                            value={window.startTime}
                            onChange={(e) => {
                              const updatedWindows = [...(editingRule.postingTimeWindows || [])];
                              updatedWindows[index] = { ...window, startTime: e.target.value };
                              setEditingRule((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      postingTimeWindows: updatedWindows,
                                    }
                                  : null
                              );
                              setModifiedTimeWindows(
                                (prev) => new Set([...prev, window.tempId || window.id])
                              );
                            }}
                            className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-cmp-ink mb-1">
                            End Time
                          </label>
                          <input
                            type="time"
                            value={window.endTime}
                            onChange={(e) => {
                              const updatedWindows = [...(editingRule.postingTimeWindows || [])];
                              updatedWindows[index] = { ...window, endTime: e.target.value };
                              setEditingRule((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      postingTimeWindows: updatedWindows,
                                    }
                                  : null
                              );
                              setModifiedTimeWindows(
                                (prev) => new Set([...prev, window.tempId || window.id])
                              );
                            }}
                            className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <button
                          onClick={() => {
                            if (window.id) {
                              trackTimeWindowChange('deleted', window);
                            }
                            const updatedWindows = (editingRule.postingTimeWindows || []).filter(
                              (_, i) => i !== index
                            );
                            setEditingRule((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    postingTimeWindows: updatedWindows,
                                  }
                                : null
                            );
                          }}
                          className="p-2 rounded hover:bg-cmp-bronze-soft text-error mt-6"
                        >
                          <Trash2 className="w-9" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const newWindow: PostingTimeWindow & { tempId: string } = {
                          id: '',
                          postingRuleId: editingRule.id,
                          dayOfWeek: 1,
                          startTime: '09:00',
                          endTime: '17:00',
                          isActive: true,
                          tempId: `temp_${Date.now()}_${Math.random()}`,
                        };
                        const updatedWindows = [
                          ...(editingRule.postingTimeWindows || []),
                          newWindow,
                        ];
                        setEditingRule((prev) =>
                          prev ? { ...prev, postingTimeWindows: updatedWindows } : null
                        );
                        trackTimeWindowChange('added', newWindow);
                      }}
                      className="px-3 py-1.5 border border-cmp-line-2 text-cmp-ink rounded text-sm hover:bg-form-dark flex items-center gap-2"
                    >
                      <Plus className="w-9" />
                      Add Time Window
                    </button>
                  </div>
                </details>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-6 border-t border-cmp-line-2">
              <button
                onClick={handleCloseEditDialog}
                className="px-4 py-2 bg-button-secondary text-button-secondary border border-cmp-line-2 rounded hover:bg-form-dark transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={!editingRule?.name || !editingRule?.type}
                className="px-4 py-2 bg-button-primary text-button-primary rounded font-medium hover:bg-primary-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
      {channelPicker}
    </div>
  );
}
